import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useQuiz } from '../context/QuizContext';
import {
  DASHBOARD_NAV,
  ADMIN_NAV_ITEM,
  groupContainsPath,
  openChainFor,
  descendantGroupIds,
} from '../navigation';
import { useIsAdmin } from '../../../hooks/useIsAdmin';
import { useNotifications } from '../../../contexts/NotificationsContext';

// Unread-new alert badge (10-06), matching the desktop ribbon: a red "!" dot
// on the Red Flag Alerts group, the count on the Fraud Alerts item. Hot scam
// pages count toward it too. Clears as the reader opens each item.
const BADGED_GROUP = 'red-flag-alerts';
const BADGED_PATH = '/dashboard/fraud-alerts';

const AlertBadge = ({ count, dot = false }) => {
  if (!count) return null;
  const label = `${count} new alert${count === 1 ? '' : 's'} not yet opened`;
  return (
    <span
      aria-label={label}
      title={label}
      className="ml-2 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[11px] leading-none font-bold min-w-[18px] h-[18px] px-1 align-middle"
    >
      {dot ? '!' : count > 99 ? '99+' : count}
    </span>
  );
};

// Renders the shared dashboard nav (see ../navigation.js) as a two-level
// accordion (9-22 mockups): Red Flag Alerts / Detection + / My No / SCAM at
// the top, with Detection + nesting its own sub-groups.
//
// One group open per level: opening a group closes its siblings, the same
// rule the single-level sidebar had. The chain containing the current route
// opens automatically on navigation.
//
// The whole sidebar can also collapse to a narrow strip (8-22 review round).
// Collapsed state is remembered per browser. Wrapped because storage throws in
// private windows and where site data is blocked; the sidebar just opens
// expanded in that case.
const COLLAPSE_KEY = 'nuvola.dashboard.navCollapsed';

const readCollapsed = () => {
  try {
    return window.localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch (e) {
    return false;
  }
};

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isQuizActive, requestNavigation } = useQuiz();
  const isAdmin = useIsAdmin();
  const { newAlerts } = useNotifications();
  const newAlertCount = newAlerts?.count || 0;
  const [openIds, setOpenIds] = useState(() => new Set(openChainFor(location.pathname)));
  const [collapsed, setCollapsed] = useState(readCollapsed);

  useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch (e) {
      /* storage unavailable — the choice just won't survive a reload */
    }
  }, [collapsed]);

  useEffect(() => {
    const chain = openChainFor(location.pathname);
    if (chain.length) setOpenIds(new Set(chain));
  }, [location.pathname]);

  const closeGroup = (set, group) => {
    set.delete(group.id);
    descendantGroupIds(group).forEach((id) => set.delete(id));
  };

  const openGroup = (group, siblings) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      siblings.forEach((s) => {
        if (s.type === 'group' && s.id !== group.id) closeGroup(next, s);
      });
      next.add(group.id);
      return next;
    });
  };

  const toggleGroup = (group, siblings) => {
    if (openIds.has(group.id)) {
      setOpenIds((prev) => {
        const next = new Set(prev);
        closeGroup(next, group);
        return next;
      });
    } else {
      openGroup(group, siblings);
    }
  };

  // An active screening asks before leaving the page.
  const handleNavClick = (e, path) => {
    if (isQuizActive) {
      e.preventDefault();
      requestNavigation(() => navigate(path));
    }
  };

  const leafClasses = (depth) => ({ isActive }) =>
    `block px-3 py-1.5 ${depth === 0 ? 'text-base' : 'text-sm'} transition-colors ${
      isActive ? 'text-blue-600 font-medium' : 'text-black hover:bg-gray-50'
    }`;

  const renderEntries = (entries, depth) =>
    entries.map((entry) => {
      if (entry.type !== 'group') {
        return (
          <NavLink
            key={entry.path}
            to={entry.path}
            onClick={(e) => handleNavClick(e, entry.path)}
            className={leafClasses(depth)}
          >
            {entry.label}
            {entry.path === BADGED_PATH && <AlertBadge count={newAlertCount} />}
          </NavLink>
        );
      }

      const isOpen = openIds.has(entry.id);
      const isActive = groupContainsPath(entry, location.pathname);
      const labelClasses = `flex-1 text-left px-3 py-1.5 ${
        depth === 0 ? 'text-base' : 'text-sm'
      } transition-colors ${isActive ? 'text-black font-medium' : 'text-black hover:bg-gray-50'}`;

      return (
        <div key={entry.id} className={depth === 0 ? 'mt-1' : ''}>
          <div className="flex items-center">
            {entry.path ? (
              // Groups with a splash page: the label navigates (and opens the
              // group); the caret alone collapses / expands.
              <NavLink
                to={entry.path}
                onClick={(e) => {
                  handleNavClick(e, entry.path);
                  openGroup(entry, entries);
                }}
                className={labelClasses}
              >
                {entry.label}
              </NavLink>
            ) : (
              <button
                type="button"
                onClick={() => toggleGroup(entry, entries)}
                className={labelClasses}
              >
                {entry.label}
                {entry.id === BADGED_GROUP && <AlertBadge count={newAlertCount} dot />}
              </button>
            )}
            <button
              type="button"
              onClick={() => toggleGroup(entry, entries)}
              aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${entry.label}`}
              aria-expanded={isOpen}
              className="px-2 py-1.5 text-xs text-gray-500 hover:text-black"
            >
              {isOpen ? '^' : 'v'}
            </button>
          </div>

          {isOpen && (
            <div
              className={`ml-3 ${
                isActive ? 'border-l-2 border-blue-500' : 'border-l border-gray-200'
              }`}
            >
              {renderEntries(entry.items, depth + 1)}
            </div>
          )}
        </div>
      );
    });

  return (
    <div
      className={`h-full flex flex-col overflow-hidden bg-white border-r border-gray-200 flex-shrink-0 transition-[width] duration-200 ${
        collapsed ? 'w-12' : 'w-64'
      }`}
    >
      {/* Navigation heading - vertically aligned with the tagline bar and the
          Report rail heading (same pt-6 offset, top+bottom rules). The rules
          sit on the wrapper rather than the h2 so they run the full width
          including the collapse control. */}
      <div className={`pt-6 pb-2 ${collapsed ? 'px-2' : 'px-4'}`}>
        <div className="justify-between gap-2 flex items-center min-h-[2.75rem] border-t border-b border-black">
          {/* mb-0: the global stylesheet gives h2 a bottom margin, which would
              sit inside the rules and push them apart. */}
          {!collapsed && <h2 className="text-lg text-black mb-0">Navigation</h2>}
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            className="flex w-7 flex-shrink-0 items-center justify-center rounded py-0 text-black transition-colors hover:bg-gray-100"
          >
            <span aria-hidden="true" className="text-2xl leading-none">
              {collapsed ? '»' : '«'}
            </span>
          </button>
        </div>
      </div>

      {collapsed ? null : (
        <nav className="flex-1 overflow-y-auto px-2 pb-4">
          {renderEntries(DASHBOARD_NAV, 0)}
        </nav>
      )}

      {isAdmin && !collapsed && (
        <div className="px-2 pb-4 pt-2 border-t border-gray-200 flex-shrink-0">
          <NavLink
            to={ADMIN_NAV_ITEM.path}
            onClick={(e) => handleNavClick(e, ADMIN_NAV_ITEM.path)}
            className={leafClasses(0)}
          >
            {ADMIN_NAV_ITEM.label}
          </NavLink>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
